import { LOCATION_INITIALIZED } from '@angular/common';
import { inject, Injector } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { LOCAL_STORAGE_SAFARI_UPDATE_KEY } from '@app/common-modules/common-pwa/services/common-pwa-update.service';
import { INITIAL_LANGUAGE, LANGUAGE_LOCAL_STORAGE_KEY } from '@app/config';
import { environment } from '@app/environments/environment';
import { LANGUAGE_CODE } from '@app/shared/models/localization-lang';
import { LocalizationService } from '@app/shared/services/localization.service';

export async function appInitializerFactory(): Promise<void> {
  const translate = inject(TranslateService);
  const localizationService = inject(LocalizationService);
  const injector = inject(Injector);

  await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

  const url = new URL(window.location.href);
  const urlLang = url.searchParams.get('lang');
  const langToSet = urlLang || localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY) || INITIAL_LANGUAGE;

  localizationService.registerLocale(langToSet as LANGUAGE_CODE);

  firstValueFrom(localizationService.getAvailableLanguagesWithoutTesting()).then(res => {
    translate.addLangs(res.map(item => item.code));
  });

  // Safari PWA update workaround: delay first request after PWA update
  // TODO: find different solution, after safari browser pwa updated first request failed
  const wasUpdated = localStorage.getItem(LOCAL_STORAGE_SAFARI_UPDATE_KEY);
  if (wasUpdated === 'true') {
    await new Promise<void>(resolve => setTimeout(resolve, 100));
  }

  if (environment.production) {
    translate.setFallbackLang(INITIAL_LANGUAGE);
  }

  try {
    await firstValueFrom(translate.use(langToSet));
    // eslint-disable-next-line no-console
    console.info(`[INITIALIZER] Successfully initialized '${langToSet}' language.`);
  } catch {
    // eslint-disable-next-line no-console
    console.error(`[INITIALIZER] Problem with '${langToSet}' language initialization.`);
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[INITIALIZER] App language loaded.');

  const userAgent = navigator.userAgent;
  const { vendor } = navigator;
  if (/Safari/i.test(userAgent) && /Apple Computer/.test(vendor) && !/Mobi|Android/i.test(userAgent)) {
    localStorage.setItem(LOCAL_STORAGE_SAFARI_UPDATE_KEY, 'false');
  }
}
